import React, { ReactNode, useMemo } from "react";
import SectionHeader from "../../custom/SeactionHeader";
import SkeletonLoader from "../../custom/Skeleton";
import Advertisement from "../../layout/notifications/Advertisement";
import CategoryFilter from "../../custom/CategoryFilter";
import { useAppDispatch, useAppSelector } from "@/src/store/hooks";
import { RootState } from "@/src/store";
import { transformCategories } from "@/src/utilities/helper/helper";
import {
  updateBlogCategory,
  updateBlogSubcategory,
  updateCaseStudyCategory,
  updateCaseStudySubcategory,
  updateWhitepaperCategory,
  updateWhitepaperSubcategory,
  updateWebinarCategory,
  updateWebinarSubcategory,
  updateInterviewPodcastCategory,
  updateInterviewPodcastSubcategory,
  updateInfographicsCategory,
  updateInfographicsSubcategory,
} from "@/src/store/reducers/ungatedContent/ungatedContentSlice";

interface ResourceSectionWrapperProps {
  sectionId: string;
  title: string;
  description: string;
  parentSection: string; // Used to identify which section's filter logic to use
  isLoading: boolean;
  children: ReactNode;
  sidebar?: ReactNode;
  extraContent?: ReactNode;
  customFilterUI?: ReactNode; // Optional custom filter UI to override default CategoryFilter
}

const ResourceSectionWrapper: React.FC<ResourceSectionWrapperProps> = ({
  sectionId,
  title,
  description,
  parentSection,
  isLoading,
  children,
  sidebar = <Advertisement />,
  extraContent,
  customFilterUI,
}) => {
  const dispatch = useAppDispatch();
  const { productCategories } = useAppSelector(
    (state: RootState) => state.unGatedContentStateReducer,
  );

  // Helper to get connection to Redux state slice based on parentSection
  const { selectedCategory, selectedSubcategory } = useAppSelector(
    (state: RootState) => {
      // If custom filter UI is provided, we don't need to select state here effectively
      if (customFilterUI)
        return { selectedCategory: null, selectedSubcategory: null };

      const filters = state.unGatedContentStateReducer;
      switch (parentSection) {
        case "article":
          return {
            selectedCategory: filters.blogFilters?.selectedBlogCategory,
            selectedSubcategory: filters.blogFilters?.selectedBlogSubcategory,
          };
        case "case-study":
          return {
            selectedCategory:
              filters.caseStudyFilters?.selectedCaseStudyCategory,
            selectedSubcategory:
              filters.caseStudyFilters?.selectedCaseStudySubcategory,
          };
        case "whitepapers":
          return {
            selectedCategory:
              filters.whitepaperFilters?.selectedWhitepaperCategory,
            selectedSubcategory:
              filters.whitepaperFilters?.selectedWhitepaperSubcategory,
          };
        case "webinar-videos":
          return {
            selectedCategory: filters.webinarFilters?.selectedWebinarCategory,
            selectedSubcategory:
              filters.webinarFilters?.selectedWebinarSubcategory,
          };
        case "interview-podcast":
          return {
            selectedCategory:
              filters.interviewPodcastFilters?.selectedInterviewPodcastCategory,
            selectedSubcategory:
              filters.interviewPodcastFilters
                ?.selectedInterviewPodcastSubcategory,
          };
        case "infographics":
          return {
            selectedCategory:
              filters.infographicsFilters?.selectedInfographicsCategory,
            selectedSubcategory:
              filters.infographicsFilters?.selectedInfographicsSubcategory,
          };
        default:
          return {
            selectedCategory: null,
            selectedSubcategory: null,
          };
      }
    },
  );

  const handleCategorySelect = (categoryId: string | null) => {
    // 1. Dispatch Category Update
    switch (parentSection) {
      case "article":
        dispatch(updateBlogCategory(categoryId));
        break;
      case "case-study":
        dispatch(updateCaseStudyCategory(categoryId));
        break;
      case "whitepapers":
        dispatch(updateWhitepaperCategory(categoryId));
        break;
      case "webinar-videos":
        dispatch(updateWebinarCategory(categoryId));
        break;
      case "interview-podcast":
        dispatch(updateInterviewPodcastCategory(categoryId));
        break;
      case "infographics":
        dispatch(updateInfographicsCategory(categoryId));
        break;
    }

    // 2. Find first subcategory with content > 0 and Dispatch Subcategory Update
    const categorySelected = productCategories?.find(
      (category: any) => category.documentId === categoryId,
    );
    const subcategorySelected = categorySelected?.subcategories?.find(
      (subCat: any) => subCat?.contentCount[parentSection] > 0,
    );
    const subId = subcategorySelected?.documentId || null;

    switch (parentSection) {
      case "article":
        dispatch(updateBlogSubcategory(subId));
        break;
      case "case-study":
        dispatch(updateCaseStudySubcategory(subId));
        break;
      case "whitepapers":
        dispatch(updateWhitepaperSubcategory(subId));
        break;
      case "webinar-videos":
        dispatch(updateWebinarSubcategory(subId));
        break;
      case "interview-podcast":
        dispatch(updateInterviewPodcastSubcategory(subId));
        break;
      case "infographics":
        dispatch(updateInfographicsSubcategory(subId));
        break;
    }
  };

  const handleSubcategorySelect = (subCategoryId: string) => {
    switch (parentSection) {
      case "article":
        dispatch(updateBlogSubcategory(subCategoryId));
        break;
      case "case-study":
        dispatch(updateCaseStudySubcategory(subCategoryId));
        break;
      case "whitepapers":
        dispatch(updateWhitepaperSubcategory(subCategoryId));
        break;
      case "webinar-videos":
        dispatch(updateWebinarSubcategory(subCategoryId));
        break;
      case "interview-podcast":
        dispatch(updateInterviewPodcastSubcategory(subCategoryId));
        break;
      case "infographics":
        dispatch(updateInfographicsSubcategory(subCategoryId));
        break;
    }
  };

  return (
    <section id={sectionId} className="bg-muted py-12 md:py-24">
      <div className="flex flex-col flex-1 w-[95%] max-w-7xl mx-auto">
        {/* Section Header */}
        <SectionHeader title={title} description={description} />

        {/* Category Filters */}
        {customFilterUI ? (
          customFilterUI
        ) : (
          <CategoryFilter
            variant="horizontal"
            categories={transformCategories(productCategories)}
            subCategoryKey="subcategories"
            selectedCategoryId={selectedCategory || ""}
            selectedSubCategoryId={selectedSubcategory || ""}
            onCategorySelect={handleCategorySelect}
            onSubCategorySelect={handleSubcategorySelect}
            parentSection={parentSection}
          />
        )}

        {isLoading ? (
          <div className="py-12 md:py-20">
            <SkeletonLoader />
          </div>
        ) : (
          <div className="grid lg:grid-cols-4 gap-8 px-4">
            {/* Main Content */}
            <div className="lg:col-span-3">{children}</div>

            {/* Sidebar */}
            <div className="lg:col-span-1 mt-8">{sidebar}</div>
          </div>
        )}
      </div>
      {extraContent}
    </section>
  );
};

export default ResourceSectionWrapper;
